import {
  type Consultant,
  type CreateLabTestInput,
  type Department,
  ENUM_LABELS,
  toOptions,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { CheckboxPicker } from '#client/components/ui/CheckboxPicker';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { choiceToId, idToChoice } from './lab-test-form.logic';
import type { SetField } from '#client/hooks/useFormValues';

interface FieldsProps {
  values: CreateLabTestInput;
  onFieldChange: SetField<CreateLabTestInput>;
  errors: Record<string, string>;
}

interface DetailsCardProps extends FieldsProps {
  departments: readonly Department[];
  consultants: readonly Consultant[];
}

/** Name, price, turnaround, grouping and the notes printed for the patient. */
export function LabTestDetailsCard({
  values,
  onFieldChange,
  errors,
  departments,
  consultants,
}: DetailsCardProps) {
  return (
    <Card title="Lab test">
      <div className="c-form-grid">
        <TextField
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          required
        />
        <TextField
          label="Code"
          value={values.code}
          error={errors.code}
          onChange={(event) => onFieldChange('code', event.target.value)}
          required
        />
        <NumberField
          label="Price"
          min={0}
          step={0.01}
          value={values.price}
          error={errors.price}
          onChange={(price) => onFieldChange('price', price)}
          prefix="Rs"
          required
        />
        <NumberField
          label="Turnaround time"
          min={1}
          max={720}
          step={1}
          nullable
          value={values.turnaround_hours}
          error={errors.turnaround_hours}
          // Empty means "no committed turnaround" — null, not 0. A zero
          // would make every report for this test instantly overdue.
          onChange={(hours) => onFieldChange('turnaround_hours', hours)}
          suffix="hours"
          helpText="Leave blank if this test has no committed turnaround"
        />
        <Select
          label="Department"
          options={departments.map((department) => ({
            value: String(department.id),
            label: department.name,
          }))}
          placeholder="No department"
          value={idToChoice(values.department_id)}
          error={errors.department_id}
          onChange={(choice) => onFieldChange('department_id', choiceToId(choice))}
          helpText="Groups this test on the printed rate list"
        />
        <Select
          label="Reporting doctor"
          options={consultants.map((consultant) => ({
            value: String(consultant.id),
            label: `${consultant.name} — ${consultant.designation}`,
          }))}
          placeholder="None"
          value={idToChoice(values.default_consultant_id)}
          error={errors.default_consultant_id}
          onChange={(choice) => onFieldChange('default_consultant_id', choiceToId(choice))}
          helpText="Copied onto new reports. Changing it never affects reports already signed."
        />
        <ReportKindField values={values} onFieldChange={onFieldChange} errors={errors} />
      </div>

      <LabTestNotesFields values={values} onFieldChange={onFieldChange} errors={errors} />
    </Card>
  );
}

function ReportKindField({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Select
      label="Report type"
      options={toOptions(ENUM_LABELS.reportKind)}
      value={values.report_kind}
      error={errors.report_kind}
      onChange={(kind) => onFieldChange('report_kind', kind)}
      helpText="A culture records an organism and an antibiotic panel instead of parameter rows"
    />
  );
}

/** The free-text notes: one for the report, one printed on the patient's slip. */
function LabTestNotesFields({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <>
      <TextArea
        label="Comment"
        value={values.comment ?? ''}
        error={errors.comment}
        onChange={(event) => onFieldChange('comment', event.target.value)}
        rows={3}
      />

      <TextArea
        label="Sample instructions"
        value={values.sample_instructions ?? ''}
        error={errors.sample_instructions}
        onChange={(event) => onFieldChange('sample_instructions', event.target.value)}
        rows={2}
        helpText="Printed on the registration slip of every patient who books this test, so write it for them. Leave blank if there is no preparation."
      />
    </>
  );
}

interface PickerCardProps<T extends { id: number; name: string; code: string }> {
  title: string;
  subtitle: string;
  /** Plural, for the search box and the empty state. */
  noun: string;
  items: T[];
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  error: string | undefined;
  isLoading: boolean;
  loadError: string | undefined;
}

/** The test's parameters, or a culture's antibiotic panel: a searchable checklist. */
export function LabTestPickerCard<T extends { id: number; name: string; code: string }>({
  title,
  subtitle,
  noun,
  items,
  selectedIds,
  onSelectedIdsChange,
  error,
  isLoading,
  loadError,
}: PickerCardProps<T>) {
  return (
    <Card title={title} subtitle={subtitle}>
      {loadError && <Banner tone="critical">{loadError}</Banner>}

      <CheckboxPicker
        items={items}
        selectedIds={selectedIds}
        onChange={onSelectedIdsChange}
        label={(item) => `${item.name} (${item.code})`}
        searchText={(item) => item.code}
        loading={isLoading}
        searchPlaceholder={`Search ${noun}`}
        emptyState={`No ${noun} defined yet`}
        error={error}
      />
    </Card>
  );
}
